import { assertNever } from "@bindery/shared/std/assert-never";
import { RESAY_CAP, tokenHistory, tokenLineage } from "./lineage";
import {
  isCapitalized,
  isWordText,
  type Composition,
  type CompositionEdit,
  type CompositionToken,
  type IntentJudgement,
  type UnresolvedToken,
  type WordToken,
} from "./schema";

export type EditError =
  | { readonly kind: "unknown-token"; readonly tokenId: string }
  | { readonly kind: "duplicate-token"; readonly tokenId: string }
  | { readonly kind: "not-a-word"; readonly tokenId: string }
  | { readonly kind: "not-unresolved"; readonly tokenId: string }
  | { readonly kind: "not-open"; readonly tokenId: string }
  | {
      readonly kind: "stale";
      readonly tokenId: string;
      readonly expected: string;
      readonly actual: string;
    }
  | { readonly kind: "index-out-of-range"; readonly index: number }
  | { readonly kind: "span-not-contiguous" }
  | { readonly kind: "re-say-cap"; readonly tokenIds: readonly string[] }
  | { readonly kind: "invalid-text"; readonly text: string };

export type EditResult =
  | {
      readonly tag: "ok";
      readonly value: Composition;
      /** Ids a `judge` edit named that are no longer words here. */
      readonly skipped?: readonly string[];
    }
  | { readonly tag: "err"; readonly error: EditError };

type Tokens = readonly CompositionToken[];
type TokensResult =
  | { readonly tag: "ok"; readonly value: CompositionToken[] }
  | { readonly tag: "err"; readonly error: EditError };

const ok = (value: CompositionToken[]): TokensResult => ({ tag: "ok", value });
const err = (error: EditError): TokensResult => ({ tag: "err", error });

const indexOfToken = (tokens: Tokens, id: string): number =>
  tokens.findIndex((t) => t.id === id);

const personJudgement = (
  source: "child" | "adult",
  at: number,
): IntentJudgement => ({
  source,
  model: null,
  intendedConfidence: 1,
  alternatives: [],
  at,
});

function updateWord(
  tokens: Tokens,
  tokenId: string,
  update: (word: WordToken) => WordToken | EditError,
): TokensResult {
  const index = indexOfToken(tokens, tokenId);
  const token = tokens[index];
  if (token === undefined) return err({ kind: "unknown-token", tokenId });
  if (token.kind !== "word") return err({ kind: "not-a-word", tokenId });
  const next = update(token);
  if (next.kind !== "word") return err(next);
  return ok(tokens.map((t, i) => (i === index ? next : t)));
}

function updateWords(
  tokens: Tokens,
  tokenIds: readonly string[],
  update: (word: WordToken) => WordToken,
): TokensResult {
  const ids = new Set(tokenIds);
  for (const id of ids) {
    const token = tokens[indexOfToken(tokens, id)];
    if (token === undefined) return err({ kind: "unknown-token", tokenId: id });
    if (token.kind !== "word") return err({ kind: "not-a-word", tokenId: id });
  }
  return ok(
    tokens.map((t) => (t.kind === "word" && ids.has(t.id) ? update(t) : t)),
  );
}

function rewrite(
  tokens: Tokens,
  tokenId: string,
  from: string,
  to: string,
  update: (word: WordToken) => WordToken,
): TokensResult {
  if (!isWordText(to)) return err({ kind: "invalid-text", text: to });
  return updateWord(tokens, tokenId, (word) =>
    word.text === from
      ? update({ ...word, text: to })
      : { kind: "stale", tokenId, expected: from, actual: word.text },
  );
}

function insertAt(
  tokens: Tokens,
  index: number,
  inserted: Tokens,
): TokensResult {
  if (index > tokens.length) return err({ kind: "index-out-of-range", index });
  const seen = new Set(tokens.map((t) => t.id));
  const duplicate = inserted.find((t) => {
    const repeated = seen.has(t.id);
    seen.add(t.id);
    return repeated;
  });
  if (duplicate !== undefined) {
    return err({ kind: "duplicate-token", tokenId: duplicate.id });
  }
  return ok([...tokens.slice(0, index), ...inserted, ...tokens.slice(index)]);
}

/** Positions of the ids in document order, or an error naming a missing one. */
function positions(
  tokens: Tokens,
  tokenIds: readonly string[],
): { tag: "ok"; value: number[] } | { tag: "err"; error: EditError } {
  const found: number[] = [];
  for (const id of new Set(tokenIds)) {
    const index = indexOfToken(tokens, id);
    if (index < 0)
      return { tag: "err", error: { kind: "unknown-token", tokenId: id } };
    found.push(index);
  }
  return { tag: "ok", value: found.sort((a, b) => a - b) };
}

const isContiguous = (sorted: readonly number[]): boolean =>
  sorted.every((p, i) => i === 0 || p === (sorted[i - 1] ?? -2) + 1);

/**
 * Replaces exactly the span: confirmed neighbours of a re-said sentence keep
 * their tokens, and each new word carries the span's lineage so the
 * correction history follows it.
 */
function replaceSpan(
  tokens: Tokens,
  tokenIds: readonly string[],
  replacement: Tokens,
): TokensResult {
  const found = positions(tokens, tokenIds);
  if (found.tag === "err") return err(found.error);
  if (!isContiguous(found.value)) return err({ kind: "span-not-contiguous" });
  const first = found.value[0] ?? 0;
  const span = found.value.flatMap((p) => {
    const token = tokens[p];
    return token === undefined ? [] : [token];
  });
  const lineage = [...new Set(span.flatMap(tokenLineage))];
  const lineaged = replacement.map((t): CompositionToken => {
    switch (t.kind) {
      case "word":
        return { ...t, origin: "re-said", supersedes: lineage };
      case "unresolved":
        return { ...t, supersedes: lineage };
      case "punct":
        return t;
      default:
        return assertNever(t, "token");
    }
  });
  const without = tokens.filter((_, i) => !found.value.includes(i));
  return insertAt(without, first, lineaged);
}

function saveForAdult(tokens: Tokens, tokenId: string): TokensResult {
  const index = indexOfToken(tokens, tokenId);
  const token = tokens[index];
  if (token === undefined) return err({ kind: "unknown-token", tokenId });
  if (token.kind !== "word") return err({ kind: "not-a-word", tokenId });
  const slot: UnresolvedToken = {
    kind: "unresolved",
    id: token.id,
    cause: "saved-for-adult",
    asr: token.asr,
    supersedes: token.supersedes,
  };
  return ok(tokens.map((t, i) => (i === index ? slot : t)));
}

function adultResolve(
  tokens: Tokens,
  tokenId: string,
  text: string,
  at: number,
): TokensResult {
  if (!isWordText(text)) return err({ kind: "invalid-text", text });
  const index = indexOfToken(tokens, tokenId);
  const token = tokens[index];
  if (token === undefined) return err({ kind: "unknown-token", tokenId });
  if (token.kind !== "unresolved")
    return err({ kind: "not-unresolved", tokenId });
  const word: WordToken = {
    kind: "word",
    id: token.id,
    text,
    origin: "adult",
    asr: token.asr,
    intent: [personJudgement("adult", at)],
    resolution: "confirmed",
    properNoun: isCapitalized(text),
    supersedes: token.supersedes,
  };
  return ok(tokens.map((t, i) => (i === index ? word : t)));
}

function move(
  tokens: Tokens,
  tokenIds: readonly string[],
  toIndex: number,
): TokensResult {
  const found = positions(tokens, tokenIds);
  if (found.tag === "err") return err(found.error);
  const moved = found.value.flatMap((p) => {
    const token = tokens[p];
    return token === undefined ? [] : [token];
  });
  const rest = tokens.filter((_, i) => !found.value.includes(i));
  return insertAt(rest, toIndex, moved);
}

function unchangedSpan(
  tokens: Tokens,
  tokenIds: readonly string[],
): TokensResult {
  const found = positions(tokens, tokenIds);
  if (found.tag === "err") return err(found.error);
  if (!isContiguous(found.value)) return err({ kind: "span-not-contiguous" });
  return ok([...tokens]);
}

/**
 * Why a span can no longer be re-said: a word gone, not a word, or off the
 * ladder because the child confirmed it or saved it for a grown-up.
 */
function closedSpan(
  tokens: Tokens,
  tokenIds: readonly string[],
): EditError | null {
  for (const tokenId of tokenIds) {
    const token = tokens[indexOfToken(tokens, tokenId)];
    if (token === undefined) return { kind: "unknown-token", tokenId };
    if (token.kind === "punct") return { kind: "not-a-word", tokenId };
    if (token.kind === "unresolved" || token.resolution === "confirmed") {
      return { kind: "not-open", tokenId };
    }
  }
  return null;
}

function reSay(
  tokens: Tokens,
  edit: Extract<CompositionEdit, { op: "re-say" }>,
): TokensResult {
  const closed = closedSpan(tokens, edit.tokenIds);
  if (closed !== null) return err(closed);
  return edit.result.outcome === "replaced"
    ? replaceSpan(tokens, edit.tokenIds, edit.result.tokens)
    : unchangedSpan(tokens, edit.tokenIds);
}

function remove(tokens: Tokens, tokenIds: readonly string[]): TokensResult {
  const found = positions(tokens, tokenIds);
  if (found.tag === "err") return err(found.error);
  return ok(tokens.filter((_, i) => !found.value.includes(i)));
}

const confirmed =
  (source: "child" | "adult", at: number) =>
  (word: WordToken): WordToken => ({
    ...word,
    resolution: "confirmed",
    intent: [...word.intent, personJudgement(source, at)],
  });

function applyToTokens(tokens: Tokens, edit: CompositionEdit): TokensResult {
  switch (edit.op) {
    case "insert-dictation":
      return insertAt(tokens, edit.atIndex, edit.tokens);
    case "insert-typed":
      if (!isWordText(edit.text)) {
        return err({ kind: "invalid-text", text: edit.text });
      }
      return insertAt(tokens, edit.atIndex, [
        {
          kind: "word",
          id: edit.tokenId,
          text: edit.text,
          origin: "typed",
          asr: null,
          intent: [personJudgement("child", edit.at)],
          resolution: "confirmed",
          properNoun: false,
          supersedes: [],
        },
      ]);
    case "judge":
      return edit.judgements.reduce<TokensResult>(
        (acc, { tokenId, judgement }) =>
          acc.tag === "err"
            ? acc
            : updateWord(acc.value, tokenId, (word) => ({
                ...word,
                intent: [...word.intent, judgement],
              })),
        ok([...tokens]),
      );
    case "surface":
      return updateWords(tokens, edit.tokenIds, (word) =>
        word.resolution === "confirmed"
          ? word
          : { ...word, resolution: "surfaced" },
      );
    case "confirm":
      return updateWord(tokens, edit.tokenId, confirmed("child", edit.at));
    case "choose":
    case "type":
      return rewrite(tokens, edit.tokenId, edit.from, edit.to, (word) => ({
        ...confirmed("child", edit.at)(word),
        origin: edit.op === "choose" ? "chosen" : "typed",
      }));
    case "re-say":
      return reSay(tokens, edit);
    case "describe": {
      const { result } = edit;
      if (result.outcome !== "chosen") {
        return updateWord(tokens, edit.tokenId, (word) => word);
      }
      const suggestion: IntentJudgement = {
        source: "description-lm",
        model: result.model,
        generator: result.generator,
        intendedConfidence: null,
        alternatives: result.candidates.map((word) => ({
          word,
          probability: null,
        })),
        at: edit.at,
      };
      return updateWord(tokens, edit.tokenId, (word) =>
        isWordText(result.to)
          ? {
              ...word,
              text: result.to,
              origin: "chosen",
              resolution: "confirmed",
              intent: [
                ...word.intent,
                suggestion,
                personJudgement("child", edit.at),
              ],
            }
          : { kind: "invalid-text", text: result.to },
      );
    }
    case "save-for-adult":
      return saveForAdult(tokens, edit.tokenId);
    case "adult-resolve":
      return adultResolve(tokens, edit.tokenId, edit.text, edit.at);
    case "reject":
      return updateWord(tokens, edit.tokenId, (word) => word);
    case "build":
      return updateWord(tokens, edit.tokenId, (word) => word);
    case "delete":
      return remove(tokens, edit.tokenIds);
    case "move":
      return move(tokens, edit.tokenIds, edit.toIndex);
    default:
      return assertNever(edit, "edit");
  }
}

type JudgeEdit = Extract<CompositionEdit, { op: "judge" }>;

// Judgements arrive after the sentence, by which time the child may have
// re-said or deleted some of the words; the rest must keep theirs.
function applyJudge(composition: Composition, edit: JudgeEdit): EditResult {
  const isWord = (id: string): boolean =>
    composition.tokens.some((t) => t.id === id && t.kind === "word");
  const [first, ...rest] = edit.judgements.filter((j) => isWord(j.tokenId));
  const skipped = edit.judgements
    .filter((j) => !isWord(j.tokenId))
    .map((j) => j.tokenId);
  if (first === undefined) return { tag: "ok", value: composition, skipped };
  const applied = applyRecorded(composition, {
    ...edit,
    judgements: [first, ...rest],
  });
  return applied.tag === "ok" ? { ...applied, skipped } : applied;
}

// Every re-say of the span or of any word it replaced, so a replacement
// cannot reset the count and the re-say loop the cap exists for cannot start.
function reSaysOf(composition: Composition, tokenIds: readonly string[]) {
  const span = new Set(tokenIds);
  const lineage = composition.tokens
    .filter((t) => span.has(t.id))
    .flatMap(tokenLineage);
  return tokenHistory(composition.edits, lineage).filter(
    (e) => e.op === "re-say",
  ).length;
}

function applyRecorded(
  composition: Composition,
  edit: CompositionEdit,
): EditResult {
  if (
    edit.op === "re-say" &&
    reSaysOf(composition, edit.tokenIds) >= RESAY_CAP
  ) {
    return {
      tag: "err",
      error: { kind: "re-say-cap", tokenIds: edit.tokenIds },
    };
  }
  const result = applyToTokens(composition.tokens, edit);
  if (result.tag === "err") return result;
  return {
    tag: "ok",
    value: {
      ...composition,
      tokens: result.value,
      edits: [...composition.edits, edit],
    },
  };
}

/**
 * Applies one edit and records it in the log. An edit that names a token
 * that is not there, or text that is no longer what it claims to replace,
 * is refused and the composition is unchanged. `judge` is the exception: it
 * attaches what it can, logs only that, and reports the ids it skipped.
 */
export function applyEdit(
  composition: Composition,
  edit: CompositionEdit,
): EditResult {
  return edit.op === "judge"
    ? applyJudge(composition, edit)
    : applyRecorded(composition, edit);
}

/** Applies edits in order, stopping at the first refusal. */
export function replay(
  base: Composition,
  edits: readonly CompositionEdit[],
): EditResult {
  return edits.reduce<EditResult>(
    (acc, edit) => (acc.tag === "err" ? acc : applyEdit(acc.value, edit)),
    { tag: "ok", value: base },
  );
}
